import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  MenuItem,
  Alert,
  Divider,
  InputAdornment,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useNavigate } from "react-router-dom";
import { getUsername } from "../../Utility/helper";

const loanTypes = [
  "Home Loan",
  "LAP",
  "Personal Loan",
  "Car Loan",
  "Business Loan",
];

const NewLeadForm = () => {
  const [lead, setLead] = useState({
    name: "",
    mobile: "",
    loan_type: "",
    amount: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setLead({ ...lead, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setStatus({ type: "", message: "" });
    setIsLoading(true);

    const { name, mobile, loan_type, amount } = lead;
    const username = getUsername();

    if (!username || !name || !mobile || !loan_type || !amount) {
      setStatus({
        type: "error",
        message: "Please fill in all required fields",
      });
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(
        "https://core-bankers-backend.vercel.app/create-lead",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...lead, username }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create lead");

      setStatus({ type: "success", message: "Lead created successfully" });
      setLead({ name: "", mobile: "", loan_type: "", amount: "" });

      navigate("/upload-docs", { state: { leadId: data.lead_id } });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(to right, #007BFF, #00C6FF)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 500,
          width: "100%",
          borderRadius: 4,
          backgroundColor: "#fff",
        }}
      >
        {/* Header Section */}
        <Box textAlign="center" mb={3}>
         <Typography variant="h5" fontWeight="bold">
            Create New Lead
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the details to create a new loan lead
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {status.message && (
          <Alert severity={status.type} sx={{ mb: 2 }}>
            {status.message}
          </Alert>
        )}

        {/* Form Fields */}
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            name="name"
            label="Full Name"
            value={lead.name}
            onChange={handleChange}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            name="mobile"
            label="Mobile Number"
            type="tel"
            value={lead.mobile}
            onChange={handleChange}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            name="loan_type"
            label="Loan Type"
            value={lead.loan_type}
            onChange={handleChange}
            fullWidth
            required
          >
            {loanTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            name="amount"
            label="Loan Amount (₹)"
            type="number"
            value={lead.amount}
            onChange={handleChange}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MonetizationOnIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              py: 1.5,
              mt: 2,
              fontWeight: "bold",
              borderRadius: "8px",
              fontSize: "1rem",
              textTransform: "none",
              background:
                "linear-gradient(90deg, #007BFF 0%, #0056b3 100%)",
              "&:hover": {
                background: "linear-gradient(90deg, #0056b3, #003f88)",
              },
            }}
          >
            {isLoading ? "Submitting..." : "Submit Lead"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default NewLeadForm;
